import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, ApiError, setCsrfToken } from '../lib/api';
import type { Permission, Role } from '../../../shared/types';

export interface AuthUser {
  id: string;
  email: string;
  fullName?: string;
  role: Role;
  organisationId: string;
  permissions: Permission[];
}

interface AuthValue {
  user: AuthUser | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api<{ user: AuthUser; csrfToken?: string }>('/api/auth/me');
      setUser(r.user);
      if (r.csrfToken) setCsrfToken(r.csrfToken);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const r = await api<{ user: AuthUser; csrfToken?: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(r.user);
    if (r.csrfToken) setCsrfToken(r.csrfToken);
  }, []);

  const signOut = useCallback(async () => {
    try { await api('/api/auth/logout', { method: 'POST' }); } finally { setUser(null); setCsrfToken(null); }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
